import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { capitalize, isNull, toNumber } from "lodash";
import { scroller } from "react-scroll";

import type { BadgeProps } from "@krea/shared/design-system/Badge";
import { CountryCode, PaymentFrequency } from "@krea/shared/types/common";
import { FormatNumber } from "@krea/shared/utils/formatNumber";

import {
  AmortizationMethodEnum,
  LenderApplicationsFilterEnum,
  LenderApplicationTypeEnum,
  RealEstateAmortizationMethodEnum,
} from "@/features/common/enums";

dayjs.extend(relativeTime);

export const getCompanyDetails = (country: CountryCode) => {
  switch (country) {
    case CountryCode.SE:
      return {
        companyName: "Krea",
        phone: { number: "08-551 082 62", link: "+46855108262" },
        email: "kundservice@krea.se",
        address: {
          title: "Valhallavägen 60, 114 27 Stockholm",
          link: "https://maps.google.com/?q=KREA%20F%C3%B6retagsl%C3%A5n%20Valhallav%C3%A4gen%2060,%20114%2027%20Stockholm",
        },
        openingHours: [
          { day: "Monday - Thursday", time: "09 - 18" },
          { day: "Friday", time: "09 - 17" },
        ],
      };

    case CountryCode.FI:
      return {
        companyName: "Kredita",
        phone: { number: "09 315 316 10", link: "0931531610" },
        email: "asiakaspalvelu@kredita.fi",
        address: {
          title: "Linnoitustie 6, 02600 Espoo",
          link: "https://www.google.com/maps/place/Wonderland+Work+Lepp%C3%A4vaara/@60.2117072,24.8134392,17z/data=!4m15!1m8!3m7!1s0x468df5d7e2cae661:0x98e466774ad35947!2sLinnoitustie+6,+02600+Espoo,+Finland!3b1!8m2!3d60.2117072!4d24.8134392!16s%2Fg%2F11c88dc9hv!3m5!1s0x468df5bf2d23861b:0x8d3c916b48882443!8m2!3d60.2119617!4d24.8147542!16s%2Fg%2F11ng59wsqy",
        },
        openingHours: [{ day: "Monday - Friday", time: "10 - 16" }],
      };

    default:
      return {};
  }
};

const ScrollToElement = (
  element: string,
  containerId: string = "main-content-scroll-container",
) => {
  scroller.scrollTo(element, {
    duration: 500,
    delay: 100,
    smooth: true,
    offset: -24,
    containerId: containerId,
  });
};

const getGuarantorStringById = (
  guarantors: Record<string, string | number>[],
  id: number,
) => {
  const guarantor = guarantors.find(
    (guarantor: Record<string, string | number>) =>
      guarantor.id === toNumber(id),
  );
  if (!guarantor) return "";

  return `${guarantor.ssn} ${guarantor.first_name ? guarantor.first_name : ""} ${
    guarantor.last_name ? guarantor.last_name : ""
  } ${guarantor.email ? `- ${guarantor.email}` : ""}`;
};

const getTitleAndDescriptionForFilters = (
  filter: LenderApplicationsFilterEnum,
  count?: number,
) => {
  switch (filter) {
    case LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS:
      return {
        title: `New loan applications ${count ? `(${count})` : ""}`,
        description:
          "These are loan applications you haven't bidded on or rejected yet.",
      };
    case LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS:
      return {
        title: `New factoring applications ${count ? `(${count})` : ""}`,
        description:
          "These are factoring applications you haven't bidded on or rejected yet.",
      };
    case LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS:
      return {
        title: `New real estate applications ${count ? `(${count})` : ""}`,
        description:
          "These are real estate applications you haven't bidded on or rejected yet.",
      };

    case LenderApplicationsFilterEnum.NO_LEASING_BIDS:
      return {
        title: `New leasing applications ${count ? `(${count})` : ""}`,
        description:
          "These are leasing applications you haven't bidded on or rejected yet.",
      };
    case LenderApplicationsFilterEnum.BID_SELECTED:
      return {
        title: `Won applications ${count ? `(${count})` : ""}`,
        description:
          "You have won these applications, but have neither marked them as paid out nor rejected them.",
      };
    case LenderApplicationsFilterEnum.HAS_OWN_BIDS:
      return {
        title: `Active Bids ${count ? `(${count})` : ""}`,
        description:
          "These are applications you have active bids on, but the customer has not selected any lender yet.",
      };
    case LenderApplicationsFilterEnum.PAID_OUT:
      return {
        title: `Paid out applications ${count ? `(${count})` : ""}`,
        description: "These are loans you have paid out.",
      };
    case LenderApplicationsFilterEnum.REJECTED:
      return {
        title: `Rejected by lender applications ${count ? `(${count})` : ""}`,
        description: "These are loan applications you have rejected.",
      };
    case LenderApplicationsFilterEnum.CLOSED_BY_CUSTOMER:
      return {
        title: `Closed by customer applications ${count ? `(${count})` : ""}`,
        description:
          "These are the loan applications the customer closed which have not been rejected by you. Applications rejected by you and then closed by the customer are in the 'Rejected by lender' section.",
      };

    default:
      return {
        title: "Lender applications",
        description: null,
      };
  }
};

const getVariantByLenderApplicationType = (
  type?: LenderApplicationTypeEnum,
): BadgeProps["variant"] => {
  switch (type) {
    case LenderApplicationTypeEnum.FAILED_LENDER_APPLICATION_FILTER:
      return "secondary";
    case LenderApplicationTypeEnum.NO_LENDER_RESPONSES:
      return "default";
    case LenderApplicationTypeEnum.HAS_LENDER_RESPONSES:
      return "secondary";
    case LenderApplicationTypeEnum.REJECTED:
      return "destructive";
    case LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED:
      return "default";
    case LenderApplicationTypeEnum.OTHER_LENDER_APPLICATION_BID_SELECTED:
    case LenderApplicationTypeEnum.OTHER_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER:
      return "outline";
    case LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER:
      return "success";
    case LenderApplicationTypeEnum.CLOSED_BY_CUSTOMER:
      return "outline";
    default:
      return "secondary";
  }
};

const getStatusTextAndDescriptionByType = (
  type?: LenderApplicationTypeEnum,
  lenderResponsesExists?: boolean,
) => {
  switch (type) {
    case LenderApplicationTypeEnum.FAILED_LENDER_APPLICATION_FILTER:
      return {
        label: "Filtered",
        description:
          "These applications were caught in a filter you requested Krea to set up for you",
      };
    case LenderApplicationTypeEnum.NO_LENDER_RESPONSES:
      return {
        label: "New",
        description:
          "You haven't responded to this loan application yet! Place an offer below",
      };
    case LenderApplicationTypeEnum.HAS_LENDER_RESPONSES:
      return {
        label: "Active bids",
        description: "You have active bids. Place more offers below",
      };
    case LenderApplicationTypeEnum.REJECTED:
      return {
        label: "Rejected",
        description: "You rejected this loan application",
      };
    case LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED:
      return {
        label: "Won & Unpaid",
        description:
          "You have a winning bid. Contact information is now available",
      };

    case LenderApplicationTypeEnum.OTHER_LENDER_APPLICATION_BID_SELECTED:
      if (lenderResponsesExists) {
        return {
          label: "Active bids",
          description: "You have active bids. Place more offers below",
        };
      }
      return {
        label: "New",
        description:
          "You haven't responded to this loan application yet! Place an offer below",
      };
    case LenderApplicationTypeEnum.OTHER_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER:
      return {
        label: "Lost",
        description: "Nothing more to do here!",
      };
    case LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER:
      return {
        label: "Paid out",
        description: "Nothing more to do here!",
      };
    case LenderApplicationTypeEnum.CLOSED_BY_CUSTOMER:
      return {
        label: "Closed by customer",
        description: "The customer closed this application.",
      };
    default:
      return false;
  }
};

const ucFirst = (str: string) => {
  if (!str?.[0]) return str;

  return str[0].toUpperCase() + str.slice(1);
};

const formatTime = (date?: Date | string | null) => {
  return dayjs(date).format("YYYY-MM-DD HH:mm");
};

const timeTo = (end?: Date | string | null) => {
  const today = dayjs();
  const endTime = dayjs(end);
  const diffInMinutes = endTime.diff(today, "minutes");

  let restTime: string;

  if (isNull(end)) {
    restTime = "Overdue";
  } else if (diffInMinutes < 60) {
    restTime = "Still open";
  } else {
    restTime = today.to(endTime, true);
  }

  return {
    restTime: restTime,
    warning: isNull(end) || diffInMinutes < 24 * 60,
  };
};

const amortizationMethodsListCompanyLoan = [
  { label: "Annuity", value: AmortizationMethodEnum.ANNUITY },
  { label: "Straight amortization", value: AmortizationMethodEnum.LINEAR },
  { label: "Fixed fee", value: AmortizationMethodEnum.FIXED },
  { label: "Bullet loan", value: AmortizationMethodEnum.BULLET },
];

const amortizationMethodsListRealEstate = [
  {
    label: "Straight amortization",
    value: RealEstateAmortizationMethodEnum.LINEAR,
  },
  { label: "Bullet loan", value: RealEstateAmortizationMethodEnum.BULLET },
  {
    label: "Building credit (byggkreditiv)",
    value: RealEstateAmortizationMethodEnum.BUILDING_CREDIT,
  },
];

const paymentFrequencyList = [
  { label: "Monthly", value: PaymentFrequency.MONTHLY },
  { label: "At maturity", value: PaymentFrequency.AT_MATURITY },
];

// The backend rejects deferral on any method that amortises during the term.
const BULLET_LIKE_METHODS: string[] = [
  AmortizationMethodEnum.BULLET,
  AmortizationMethodEnum.BUILDING_CREDIT,
];

const isPaymentFrequencyConfigurable = (
  amortizationMethod: string | undefined,
): boolean =>
  amortizationMethod !== undefined &&
  BULLET_LIKE_METHODS.includes(amortizationMethod);

const PRODUCT = {
  COMPANY_LOAN: "COMPANY_LOAN",
  FACTORING: "FACTORING",
  REAL_ESTATE: "REAL_ESTATE",
  LEASING: "LEASING",
};

const capitalizeWithSpaces = (str: string) => {
  if (!str) {
    str = "";
  }

  return capitalize(str.replace(/[_]+/g, " "));
};

export {
  amortizationMethodsListCompanyLoan,
  amortizationMethodsListRealEstate,
  capitalizeWithSpaces,
  FormatNumber,
  formatTime,
  getGuarantorStringById,
  getStatusTextAndDescriptionByType,
  getTitleAndDescriptionForFilters,
  getVariantByLenderApplicationType,
  isPaymentFrequencyConfigurable,
  PRODUCT,
  paymentFrequencyList,
  ScrollToElement,
  timeTo,
  ucFirst,
};
